import React, { useState, useRef, useEffect } from 'react';
import { KnowledgeBotHandler } from './KnowledgeBotQueryHandler';
import { KnowledgeBotQueryCommandRequest, KnowledgeBotQueryCommandResponse} from 'Core/Api/Api';
import { SuggestionsHandler, Suggestion } from './SuggestionHandler';
import { queue } from 'jquery';

interface BotResponse {
    message: string;
    quickReplies: string[];
};

interface Message {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

const parseMarkdown = (text: string) => {
  const lines = text.split('\n'); // Split text into lines
  const elements: React.ReactNode[] = [];
  let listType: 'ul' | 'ol' | null = null; 
  let listItems: React.ReactNode[] = [];

  const flushList = () => {
    if (listItems.length > 0) {
      if (listType === 'ul') {
        elements.push(<ul key={elements.length} style={{ margin: '0', paddingLeft: '20px' }}>{listItems}</ul>);
      } else if (listType === 'ol') {
        elements.push(<ol key={elements.length} style={{ margin: '0', paddingLeft: '20px' }}>{listItems}</ol>);
      }
      listItems = [];
      listType = null;
    }
  };

  const parseInline = (line: string) => {
    const regex = /(\*\*.*?\*\*|\*.*?\*|`.*?`|\[.*?\]\(.*?\))/g;
    const parts = line.split(regex);

    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('*') && part.endsWith('*')) {
        return <em key={index}>{part.slice(1, -1)}</em>;
      }
      if (part.startsWith('`') && part.endsWith('`')) {
        return <code key={index} style={{ backgroundColor: '#e9ecef', padding: '2px 4px', borderRadius: '4px', fontSize: '85%', fontFamily: 'monospace' }}>{part.slice(1, -1)}</code>;
      }
      const linkMatch = part.match(/\[(.*?)\]\((.*?)\)/);
      if (linkMatch) {
        return <a key={index} href={linkMatch[2]} target="_blank" rel="noopener noreferrer" style={{ color: '#5865f2', textDecoration: 'underline' }}>{linkMatch[1]}</a>;
      }
      return part;
    });
  };

  lines.forEach((line, index) => {
    const ulMatch = line.match(/^[-*]\s(.*)/);
    if (ulMatch) {
      if (listType !== 'ul') {
        flushList();
        listType = 'ul';
      }
      listItems.push(<li key={index}>{parseInline(ulMatch[1])}</li>);
      return;
    }

    const olMatch = line.match(/^\d+\.\s(.*)/);
    if (olMatch) {
      if (listType !== 'ol') {
        flushList();
        listType = 'ol';
      }
      listItems.push(<li key={index}>{parseInline(olMatch[1])}</li>);
      return;
    }

    flushList();
    elements.push(<div key={index}>{parseInline(line)}</div>);
  });

  flushList();
  return elements;
};

export default function KnowledgeBot() {
  // Load messages from localStorage on component mount
  const loadMessagesFromStorage = (): Message[] => {
    try {
      const savedMessages = localStorage.getItem('knowledgebot-messages');
      if (savedMessages) {
        const parsed = JSON.parse(savedMessages);
        // Convert timestamp strings back to Date objects
        return parsed.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      }
    } catch (error) {
      console.error('Error loading messages from localStorage:', error);
    }
    
    // Return default message if no saved messages or error
    return [
      { 
        id: 1, 
        text: "Hello! I'm ready to help you with Sudoku VS or any questions you have. What can I do for you today?", 
        sender: 'bot', 
        timestamp: new Date() 
      },
    ];
  };

  const [messages, setMessages] = useState<Message[]>(loadMessagesFromStorage);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const handler = new KnowledgeBotHandler();
  const suggestionsHandler = new SuggestionsHandler();

    // Initialize with startup suggestions

  useEffect(() => {
    const startupSuggestions = suggestionsHandler.getStartupSuggestions(3);
    setSuggestions(startupSuggestions);
  }, []);

  // Save messages to localStorage whenever messages change
  useEffect(() => {
    try {
      localStorage.setItem('knowledgebot-messages', JSON.stringify(messages));
    } catch (error) {
      console.error('Error saving messages to localStorage:', error);
    }
  }, [messages]);

  // Clear chat history function
  const clearChatHistory = () => {
    const defaultMessage: Message = {
      id: Date.now(),
      text: "Hello! I'm ready to help you with Sudoku VS or any questions you have. What can I do for you today?",
      sender: 'bot',
      timestamp: new Date()
    };
    setMessages([defaultMessage]);
    // Reset to startup suggestions
    const startupSuggestions = suggestionsHandler.getStartupSuggestions(3);
    setSuggestions(startupSuggestions);
    setShowSuggestions(true);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Auto-resize textarea based on content
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 96) + 'px';
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputText]);

const getBotResponse = async (userMessage: string): Promise<BotResponse> => {
    try {
        const request: KnowledgeBotQueryCommandRequest = {
            userInput: userMessage,
        } as KnowledgeBotQueryCommandRequest;

        const response = await handler.queryKnowledgeBot(request);

        return response.success && response.message
            ? { 
                message: response.message, 
                quickReplies: response.quickReplies || [] // Ensure quickReplies is always an array
              }
            : { 
                message: "I'm sorry, I encountered an issue processing your request. Please try again.", 
                quickReplies: [] // Always provide empty array as fallback
              };
    } catch (error) {
        console.error('Error getting bot response:', error);
        return {
            message: "I'm sorry, I'm having trouble connecting right now. Please try again in a moment.",
            quickReplies: [], // Always provide empty array as fallback
        };
    }
};

const updateSuggestions = async (quickReplies: string[]) => {
    try {
        // Check if quickReplies exists and is an array before mapping
        if (quickReplies && Array.isArray(quickReplies) && quickReplies.length > 0) {
            const replacedSuggestions: Suggestion[] = quickReplies.map((text, index) => ({
                id: `suggestion-${index}`,
                text,
            }));
            
            setSuggestions(replacedSuggestions);
            setShowSuggestions(true);
        } else {
            // If quickReplies is null, undefined, empty, or not an array, hide suggestions
            setSuggestions([]);
            setShowSuggestions(false);
        }
    } catch (error) {
        console.error('Error updating suggestions:', error);
        // Hide suggestions on error
        setSuggestions([]);
        setShowSuggestions(false);
    }
};

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText || inputText.trim();
    if (!textToSend || isLoading) return;

    const userMessage: Message = {
      id: Date.now(),
      text: textToSend,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);

    try {
      const botResponseText = await getBotResponse(textToSend);
      
      const botResponse: Message = {
        id: Date.now() + 1,
        text: botResponseText.message,
        sender: 'bot',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, botResponse]);
      
      // Update suggestions based on user's message
      await updateSuggestions(botResponseText.quickReplies);
      
    } catch (error) {
      console.error('Error sending message:', error);
      
      const errorResponse: Message = {
        id: Date.now() + 1,
        text: "I'm sorry, I encountered an error processing your message. Please try again.",
        sender: 'bot',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorResponse]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    handleSendMessage(suggestion.text);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp: Date) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{ 
      height: '100%',
      display: 'flex', 
      flexDirection: 'column',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      maxWidth: '100%',
      margin: '0',
      backgroundColor: '#f8f9fa'
    }}>
      
      {/* Header */}
      <div style={{ 
        textAlign: "left",
        padding: '16px 20px',
        borderBottom: '1px solid #ccc',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        flexShrink: 0
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>🤖 Sudoku VS Assistant</h1>
          <p style={{ margin: '4px 0 8px 0', fontSize: '13px', opacity: 0.9 }}>Ready to help with your questions</p>
          <button
            onClick={clearChatHistory}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              color: 'white',
              padding: '6px 10px',
              fontSize: '11px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              backdropFilter: 'blur(10px)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            🗑️ Clear Chat
          </button>
        </div>
      </div>

      {/* Chat Area */}
      <div style={{ 
        flex: 1, 
        overflowY: 'auto', 
        padding: '16px',
        backgroundColor: '#f8f9fa',
        minHeight: 0
      }}>
        {messages.map((message) => (
          <div key={message.id} style={{ 
            marginBottom: '12px',
            display: 'flex',
            justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
              maxWidth: '85%'
            }}>
              <div style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: message.sender === 'user' ? '#667eea' : '#e9ecef',
                color: message.sender === 'user' ? 'white' : '#6c757d',
                margin: message.sender === 'user' ? '0 0 0 6px' : '0 6px 0 0',
                fontSize: '16px',
                flexShrink: 0
              }}>
                {message.sender === 'user' ? '👤' : '🤖'}
              </div>
              <div style={{
                padding: '10px 12px',
                borderRadius: '12px',
                backgroundColor: message.sender === 'user' ? '#667eea' : 'white',
                color: message.sender === 'user' ? 'white' : '#333',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                borderBottomRightRadius: message.sender === 'user' ? '4px' : '12px',
                borderBottomLeftRadius: message.sender === 'user' ? '12px' : '4px',
                wordBreak: 'break-word'
              }}>
                <div style={{ 
                  fontSize: '14px', 
                  margin: '0 0 4px 0', 
                  lineHeight: '1.4',
                  whiteSpace: 'pre-wrap',
                  textAlign: 'left'
                }}>
                    {parseMarkdown(message.text)}
                </div>
                <p style={{
                  fontSize: '11px',
                  margin: 0,
                  opacity: 0.7,
                  color: message.sender === 'user' ? '#e3e7ff' : '#6c757d'
                }}>
                  {formatTime(message.timestamp)}
                </p>
              </div>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <div style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#e9ecef',
                color: '#6c757d',
                marginRight: '6px',
                fontSize: '16px'
              }}>
                🤖
              </div>
              <div style={{
                padding: '10px 12px',
                borderRadius: '12px',
                backgroundColor: 'white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                borderBottomLeftRadius: '4px'
              }}>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <div style={{
                    width: '6px',
                    height: '6px',
                    backgroundColor: '#6c757d',
                    borderRadius: '50%',
                    animation: 'bounce-dot 1.2s infinite ease-in-out both'
                  }} />
                  <div style={{
                    width: '6px',
                    height: '6px',
                    backgroundColor: '#6c757d',
                    borderRadius: '50%',
                    animation: 'bounce-dot 1.2s infinite ease-in-out both',
                    animationDelay: '0.2s'
                  }} />
                  <div style={{
                    width: '6px',
                    height: '6px',
                    backgroundColor: '#6c757d',
                    borderRadius: '50%',
                    animation: 'bounce-dot 1.2s infinite ease-in-out both',
                    animationDelay: '0.4s'
                  }} />
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <div style={{
          padding: '8px 16px',
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e9ecef',
          flexShrink: 0
        }}>
          <div style={{
            fontSize: '12px',
            color: '#6c757d',
            marginBottom: '8px',
            fontWeight: '500'
          }}>
            💡 Suggested questions:
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px'
          }}>
            {suggestions.map((suggestion) => (
              <button
                key={suggestion.id}
                onClick={() => handleSuggestionClick(suggestion)}
                disabled={isLoading}
                style={{
                  padding: '6px 12px',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '16px',
                  fontSize: '12px',
                  color: '#4b5563',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  opacity: isLoading ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                    e.currentTarget.style.borderColor = '#667eea';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }
                }}
              >
                {suggestion.text}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Field */}
      <div style={{ 
        padding: '12px 16px',
        borderTop: '1px solid #e9ecef',
        backgroundColor: '#f8f9fa',
        flexShrink: 0
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-end',
          backgroundColor: 'white',
          borderRadius: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          padding: '6px 12px',
          border: '2px solid #e9ecef',
          transition: 'border-color 0.2s ease'
        }}
        onFocusCapture={(e) => {
          e.currentTarget.style.borderColor = '#667eea';
        }}
        onBlurCapture={(e) => {
          e.currentTarget.style.borderColor = '#e9ecef';
        }}>
          <textarea
            ref={textareaRef}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me about Sudoku VS..."
            disabled={isLoading}
            style={{
              flex: 1,
              resize: 'none',
              outline: 'none',
              border: 'none',
              color: '#333',
              fontSize: '14px',
              minHeight: '18px',
              maxHeight: '72px',
              overflowY: 'auto',
              padding: '6px',
              backgroundColor: 'transparent',
              fontFamily: 'inherit',
              lineHeight: '1.4'
            }}
            rows={1}
          />
          <button
            onClick={() => handleSendMessage()}
            disabled={!inputText.trim() || isLoading}
            style={{
              marginLeft: '8px',
              padding: '8px',
              borderRadius: '50%',
              border: 'none',
              cursor: !inputText.trim() || isLoading ? 'not-allowed' : 'pointer',
              backgroundColor: !inputText.trim() || isLoading ? '#e9ecef' : '#667eea',
              color: !inputText.trim() || isLoading ? '#6c757d' : 'white',
              boxShadow: !inputText.trim() || isLoading ? 'none' : '0 2px 4px rgba(102, 126, 234, 0.3)',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              fontSize: '14px'
            }}
          >
            ➤
          </button>
        </div>
        <div style={{
          fontSize: '10px',
          color: '#6c757d',
          marginTop: '6px',
          textAlign: 'center'
        }}>
          Press Enter to send, Shift+Enter for new line
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style>
        {`
          @keyframes bounce-dot {
            0%, 80%, 100% { 
              transform: translateY(0); 
              opacity: 0.4;
            }
            40% { 
              transform: translateY(-5px); 
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
}

